import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import LoginShell from "./LoginShell";
import { EyeOff, EyeOpen } from "./PasswordToggleIcons";
import { inputClass, linkClass, LoginVariant, submitClass } from "./loginStyles";

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: { "Content-Type": "application/json" },
});

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") || "";
  const type = searchParams.get("type") as "admin" | "branch" | null;

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const backLink = type === "admin" ? "/admin-login" : "/login";
  const variant: LoginVariant = type === "admin" ? "admin" : "branch";

  const endpoint =
    type === "admin"
      ? "admin/superemployee/reset-password"
      : "branch/employee/reset-password";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!token || !type) {
      setError("Invalid reset link.");
      return;
    }

    setLoading(true);
    try {
      await publicApi.post(endpoint, { token, newPassword });
      setSuccess(true);
      setTimeout(() => navigate(backLink), 2500);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Invalid or expired reset link.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token || !type) {
    return (
      <LoginShell
        variant="branch"
        title="Reset password"
        subtitle="This reset link is invalid or incomplete."
      >
        <span className="login-error">Invalid reset link.</span>
        <Link
          to="/login"
          className={`${linkClass("branch")} mt-4 block text-center`}
        >
          Back to login
        </Link>
      </LoginShell>
    );
  }

  return (
    <LoginShell
      variant={variant}
      title="Reset password"
      subtitle="Choose a new password for your account."
    >
      {success ? (
        <div className="flex flex-col gap-4">
          <p className="m-0 bg-[rgba(6,118,71,0.1)] px-4 py-3.5 text-[0.92rem] font-medium leading-[1.45] text-login-success">
            Password reset successfully! Redirecting to login...
          </p>
          <Link to={backLink} className={`${linkClass(variant)} text-center`}>
            Go to login
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
          <div className="flex w-full flex-col gap-[7px]">
            <label htmlFor="newPassword" className="login-label">
              New password
            </label>
            <div className="relative w-full">
              <input
                id="newPassword"
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                className={`${inputClass(variant)} pr-11`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="absolute right-2.5 top-1/2 m-0 inline-flex -translate-y-1/2 items-center justify-center border-0 bg-transparent p-1 text-[#7a8781] hover:bg-transparent hover:text-login-ink"
                onClick={() => setShowNew(!showNew)}
                aria-label={showNew ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showNew ? <EyeOff /> : <EyeOpen />}
              </button>
            </div>
          </div>

          <div className="flex w-full flex-col gap-[7px]">
            <label htmlFor="confirmPassword" className="login-label">
              Confirm password
            </label>
            <div className="relative w-full">
              <input
                id="confirmPassword"
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm new password"
                className={`${inputClass(variant)} pr-11`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="absolute right-2.5 top-1/2 m-0 inline-flex -translate-y-1/2 items-center justify-center border-0 bg-transparent p-1 text-[#7a8781] hover:bg-transparent hover:text-login-ink"
                onClick={() => setShowConfirm(!showConfirm)}
                aria-label={showConfirm ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff /> : <EyeOpen />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={submitClass(variant)}
          >
            {loading ? "Resetting..." : "Reset password"}
          </button>
          {error ? <span className="login-error">{error}</span> : null}
          <Link to={backLink} className={`${linkClass(variant)} text-center`}>
            Back to login
          </Link>
        </form>
      )}
    </LoginShell>
  );
};

export default ResetPassword;
